// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A tooltip component for asset assignment timeline segments.
 * This module provides a detailed view of assignment and project
 * events in a timeline segment, including assignment changes
 * and project dates. It visualizes events with colors and
 * connecting lines.
 *
 * Key features:
 * - Event timeline
 * - Project dates
 * - Visual indicators
 * - Event ordering
 * - Color coding
 * - Connected events
 */
import { AssetProjectHistorySegment } from '@/asset/api/get-asset-project-history';
import { useTimeline } from '@/contexts/timeline-context';
import { getInterval } from '@/project/utilities/get-interval';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Fragment } from 'react';

/**
 * Types of events that can occur in an assignment timeline.
 *
 * @typedef {string} EventType
 */
type EventType = 'assigned' | 'unassigned' | 'start' | 'end';

/**
 * Represents a single event in the assignment timeline.
 *
 * @interface Event
 */
interface Event {
  /**
   * The type of event.
   */
  type: EventType;

  /**
   * When the event occurred.
   */
  dateTime: DateTime;

  /**
   * Whether the event occurred during the project interval.
   */
  inProjectInterval: boolean;
}

/**
 * Extracts and orders events from a project history segment.
 * Combines assignment and project events into a single
 * chronological sequence.
 *
 * @param segment - The project history segment
 * @returns Ordered array of timeline events
 */
function getEvents(segment: AssetProjectHistorySegment): Event[] {
  const interval = getInterval(segment.project);

  // Start with assignment event
  const events: Event[] = [
    {
      type: 'assigned',
      dateTime: segment.start,
      inProjectInterval: interval?.contains(segment.start) ?? false,
    },
  ];

  // Add unassignment event if exists
  if (segment.end) {
    events.push({
      type: 'unassigned',
      dateTime: segment.end,
      inProjectInterval: interval?.contains(segment.end) ?? false,
    });
  }

  // Add project start if exists
  if (segment.project.startOn) {
    events.push({
      type: 'start',
      dateTime: segment.project.startOn,
      inProjectInterval: true,
    });
  }

  // Add project end if exists
  if (segment.project.endOn) {
    events.push({
      type: 'end',
      dateTime: segment.project.endOn,
      inProjectInterval: true,
    });
  }

  // Sort events chronologically
  return events.sort((event, other) => {
    return event.dateTime.toMillis() - other.dateTime.toMillis();
  });
}

/**
 * Props for configuring the AssetAssignmentTimelineTooltip component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Serial number of the asset.
   * Used in event descriptions.
   */
  serial: string;

  /**
   * The segment to display events for.
   * Contains project and assignment details.
   */
  segment: AssetProjectHistorySegment;
}

/**
 * A tooltip component for assignment timeline segments.
 * Displays a chronological list of events related to an
 * assignment, including project dates and assignment changes.
 * Uses color coding and connecting lines to visualize the
 * sequence of events.
 *
 * Features:
 * - Event timeline
 * - Project dates
 * - Visual indicators
 * - Event ordering
 * - Color coding
 * - Connected events
 * - Responsive layout
 *
 * Usage example:
 * ```tsx
 * function TimelineSegment({ segment }: Props) {
 *   return (
 *     <Tooltip arrow>
 *       <AssetAssignmentTimelineTooltip
 *         serial="ASSET-123"
 *         segment={segment}
 *       />
 *     </Tooltip>
 *   );
 * }
 * ```
 */
export function AssetAssignmentTimelineTooltip(props: Props) {
  const { palette } = useTimeline();

  // Get project interval and ordered events
  const interval = getInterval(props.segment.project);
  const events = getEvents(props.segment);

  return (
    <Stack minWidth={200}>
      <Stack
        padding={1}
        spacing={3}
        direction="row"
        justifyContent="space-between"
      >
        <Typography variant="subtitle2">
          {props.segment.project.name}
        </Typography>
        {interval && (
          <Typography variant="body2">
            {interval.toLocaleString({ dateStyle: 'medium' })}
          </Typography>
        )}
      </Stack>
      <Divider />
      <Stack padding={1}>
        {events.map((event, index) => {
          // Format date for display
          const dateLabel = event.dateTime.toLocaleString({
            dateStyle: 'medium',
          });

          // Choose color based on event context
          const color = event.inProjectInterval
            ? palette.project.solid
            : palette.assignment.solid;

          return (
            <Fragment key={event.type}>
              <Stack spacing={1} direction="row" alignItems="center">
                <Box width={8} height={8} borderRadius={4} bgcolor={color} />
                {event.type === 'assigned' && (
                  <Typography variant="body2">
                    <b>{props.serial}</b> assigned {dateLabel}
                  </Typography>
                )}
                {event.type === 'unassigned' && (
                  <Typography variant="body2">
                    <b>{props.serial}</b> unassigned {dateLabel}
                  </Typography>
                )}
                {event.type === 'start' && (
                  <Typography variant="body2">
                    Project start: {dateLabel}
                  </Typography>
                )}
                {event.type === 'end' && (
                  <Typography variant="body2">
                    Project end: {dateLabel}
                  </Typography>
                )}
              </Stack>
              {index < events.length - 1 && (
                <Box
                  width={2}
                  height={20}
                  borderRadius={2}
                  bgcolor={color}
                  marginLeft="3px"
                />
              )}
            </Fragment>
          );
        })}
      </Stack>
    </Stack>
  );
}
